/**
 * @file DiversionsModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-06
 * @description Every diversion of one customer order — from → to, when, who,
 *              why, the prices changed and the POs that went with it — opened
 *              from the Diverted pill wherever it shows (both search screens,
 *              the order window, the PO window). The reason can be changed
 *              afterwards; the old wording is kept on the record and in the
 *              order's and its POs' history (DIVERSION_REASON_EDITED).
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { diversionsOf } from '../utils/orderHub';
import './OrderHub.css';

const when = (iso) => (iso
  ? `${new Date(iso).toLocaleDateString('en-GB')} ${new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
  : '');

/** Mounts with the footer, so its slide-out buttons are measured (useSlideOutButton). */
function DiversionsBody({ list, editing, setEditing, draft, setDraft, saving, error, save }) {
  useSlideOutButton([editing, saving, list.length]);
  if (!list.length) return <p className="muted">This order has not been diverted.</p>;

  return (
    <div className="diversions-body">
      {[...list].reverse().map(d => (
        <div key={d.id} className="diversion-entry">
          <div className="diversion-head">
            <span>{d.from_name || '?'} → {d.to_name || '?'}</span>
            {editing !== d.id && (
              <button
                type="button"
                data-write-action
                className="btn-icon"
                title="Change the reason"
                onClick={() => { setEditing(d.id); setDraft(d.reason || ''); }}
              >
                <span className="material-symbols-rounded">edit</span>
              </button>
            )}
          </div>
          <div className="diversion-meta">{when(d.at)}{d.by_name ? ` by ${d.by_name}` : ''}</div>
          {editing === d.id ? (
            <>
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={2000} />
              {error && <p className="divert-error">{error}</p>}
              <div className="reason-edit-actions buttons slideout">
                <div>
                  <button className="btn-slideout" data-read-safe onClick={() => setEditing(null)} disabled={saving}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                    <span className="btn-label">Cancel</span>
                  </button>
                </div>
                <div>
                  <button className="btn-slideout" data-write-action onClick={() => save(d)} disabled={saving || !draft.trim()}
                    title={draft.trim() ? 'Save the reason' : 'A diversion needs a reason'}>
                    <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
                    <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="diversion-reason">{d.reason || <span className="muted">No reason recorded</span>}</div>
          )}
          {Array.isArray(d.price_changes) && d.price_changes.length > 0 && (
            <ul className="diversion-list">
              {d.price_changes.map(p => (
                <li key={p.line_id}>
                  {p.product || `Line ${p.line_id}`}: {formatPrice(p.old)} → {formatPrice(p.new)}
                  {p.contract ? ` (${p.contract})` : ''}
                </li>
              ))}
            </ul>
          )}
          {Array.isArray(d.purchase_orders) && d.purchase_orders.length > 0 && (
            <div className="muted">With {d.purchase_orders.map(p => p.number).join(', ')}</div>
          )}
          {Array.isArray(d.reason_edits) && d.reason_edits.length > 0 && (
            <div className="muted">
              Reason changed {d.reason_edits.length === 1 ? 'once' : `${d.reason_edits.length} times`} — last
              {' '}{when(d.reason_edits[d.reason_edits.length - 1].at)}
              {d.reason_edits[d.reason_edits.length - 1].by_name ? ` by ${d.reason_edits[d.reason_edits.length - 1].by_name}` : ''}
              {' '}(the earlier wording is in the order's history)
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * @param {number}   orderId
 * @param {string}   orderNumber
 * @param {Array}    diversions  customer_orders.diversions
 * @param {function} onClose
 * @param {function} onChanged   After a reason was saved — refresh what is behind.
 */
function DiversionsModal({ orderId, orderNumber, diversions, onClose, onChanged }) {
  const [list, setList] = useState(() => diversionsOf(diversions));
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (d) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/diversions/${encodeURIComponent(d.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: draft.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Could not save (${res.status})`);
      setList(prev => prev.map(x => (x.id === d.id ? data.diversion : x)));
      setEditing(null);
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Diversions — ${orderNumber || 'order'}`}
      size="md"
      className="diversions"
      footer={(
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Close</span>
            </button>
          </div>
        </div>
      )}
    >
      <DiversionsBody
        list={list}
        editing={editing}
        setEditing={setEditing}
        draft={draft}
        setDraft={setDraft}
        saving={saving}
        error={error}
        save={save}
      />
    </Modal>
  );
}

export default DiversionsModal;
